import { AppstoreOutlined, BarsOutlined, FilterOutlined, GlobalOutlined, MailOutlined, MessageOutlined, MobileOutlined, PlusOutlined } from '@ant-design/icons';
import { Table as AntdTable, Button, Drawer, Input, Segmented, Space, Spin, Switch, Tooltip } from 'antd';
import { debounce } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { generatePath, useLoaderData, useNavigate, useRouteLoaderData, useSearchParams } from 'react-router-dom';
import { getAllProjects, getProjectKanabanCount, getUnreadClientMessageCounts } from '../../api/project/project.js';
import { SelectedFilters } from '../../components/data/selected-filters.jsx';
import { fetchSmallWebsiteLogo } from '../../components/design/getWebsiteLogo.js';
import KanabanBoard from '../../components/design/kanaban.jsx';
import KanbanBoardSkeleton from '../../components/design/kanbanboard-skeleton.jsx';
import styles from '../../components/form/project/project-details.module.scss';
import ProjectFilterForm from '../../components/form/project/project-filter.jsx';
import { Page } from '../../components/page';
import { InfiniteTable } from '../../components/table';
import { useFetch } from '../../hooks';
import { getLoggedInUserDetails, getUserSettings, hasClientQuotationApproved, ProjectColumns, Server, setUserSettings, generateRouteUrl } from '../../library/constants';
import { ProjectKanabanColumns } from '../../library/constants/kanabanColumns/projecKanabanColumns.js';
import { getFormattedId, getRedirectWithSavedFilters, savePersistentFilters, searchParamsToObject } from '../../library/helpers/index.js';
import { formatPrice } from '../../library/utilities/intl.js';

const VIEW_MODES = Object.freeze({
  LIST: "list",
  KANABAN: "kanban",
})

const FILTER_NAMES = {
  customer_id: "Customer",
  email: "Email",
  account_manager_id: "Account Manager",
  country_id: "Country",
  language_id: "Language",
  entity_id: "Executive Entity",
  order_manager_id: "Order Manager",
  source: "Source",
  status: "Status",
  createdAt: "Created At",
  has_unseen_message: "Unseen Messages",
}

const ProjectsListView = ({ data, isLoading = false, hasMore, onChangeSort, onLoadMore, onItemClick }) => (
  <div >
    <InfiniteTable
      columns={ProjectColumns}
      data={data}
      isLoading={isLoading}
      onChange={onChangeSort}
      onRowClick={(_, record) => onItemClick(record)}
      hasMore={hasMore}
      loadMore={onLoadMore}
    />
  </div>
)
ProjectsListView.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool.isRequired,
  onChangeSort: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired
}

const renderKanabanCard = ({ item, onClick, onBadgeClick }) => {
  const getSourceIcon = () => {
    switch (item.source_id) {
      case 'email':
        return <MailOutlined className={styles.mail} />
      case 'website':
        return <GlobalOutlined className={styles.globe} />
      case 'phone':
        return <MobileOutlined className={styles.phone} />
    }
  }
  return (<div className='kanaban__card' onClick={() => onClick(item)} style={{ position: 'relative' }}>
    {/* <div className="kanaban__card__header">
      <p className='priority'>Priority : High</p>
    </div> */}
    {!!item.unread_client_messages_count && (
      <div onClick={(e) => { e.stopPropagation(); (onBadgeClick ?? onClick)(item) }} style={{
        position: 'absolute',
        top: -7,
        right: 8,
        zIndex: 1,
        lineHeight: 1,
        backgroundColor: '#ff4d4f',
        borderRadius: 10,
        padding: '2px 6px',
        boxShadow: '0 0 0 2px #fff, 0 1px 4px rgba(0,0,0,0.18)',
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        cursor: 'pointer',
      }}>
        <Tooltip title="Go to client messages">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <MessageOutlined style={{ fontSize: 10, color: '#fff', display: 'block' }} />
            <span style={{ fontSize: 9, fontWeight: 700, color: '#fff' }}>
              {item.unread_client_messages_count}
            </span>
          </span>
        </Tooltip>
      </div>
    )}
    <div className="kanaban__card__body">
      <div className="source">
        <div className="numberIcon">
          <span className="project-number">{getFormattedId(item.id)}</span>
          <span className="project-source">{getSourceIcon()}</span>
        </div>
        <span className="project-country">
          <img src={`${Server.BaseUrl}/${item.customer.country.flag_url}`} alt={item.customer.country.name} title={item.customer.country.name} />
        </span>
      </div>
      <span className="customer-logo">
        {fetchSmallWebsiteLogo(item.customer.website, item.customer.company)}
      </span>
    </div>

    <div className="kanaban__card__footer">
      <div className="details">
        <p className='head'>Account Mananger</p>
        <p className='leftHead'>Total Price</p>
        <p className='value'>{item.user?.name}</p>
        <p className='leftValue'>{formatPrice(hasClientQuotationApproved(item?.status) && item?.total_price ? item?.total_price : '0', 'EUR')}</p>
      </div>
    </div>

  </div>
  );
};

const ProjectsKanabanView = ({ data, kanabanCount, isLoading = false, hasMore, onLoadMore, onItemClick, onBadgeClick }) => {
  const columns = useMemo(() => ProjectKanabanColumns.map(column => ({
    ...column,
    items: data.filter(({ status, cancelation_request }) => (status === column.key || column.key === 100 && !!cancelation_request?.length)) ?? [],
    count: kanabanCount?.find(({ status }) => status === column.key)?.count
  })), [data, kanabanCount])

  return (
    <KanabanBoard
      columns={columns}
      onItemClick={onItemClick}
      renderItem={({ item, onClick }) => renderKanabanCard({ item, onClick, onBadgeClick })}
      hasMore={hasMore}
      isLoading={isLoading}
      loadMore={onLoadMore}
    />
  )
}
ProjectsKanabanView.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  kanabanCount: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onBadgeClick: PropTypes.func,
}

function Projects(props) {
  const initialData = useLoaderData()
  const [searchParams, setSearchParams] = useSearchParams()
  const [isPending, startTransition] = useTransition()
  const [isFiltersVisible, setIsFiltersVisible] = useState(false)
  const { user } = useRouteLoaderData('Root') ?? {}
  const navigate = useNavigate()

  //const [filtersVisible, setFiltersVisible] = useState(false)
  const [list, hasMore, isLoading, searchParamsParsed, {
    setSort,
    setFilters,
    loadMore
  }] = useFetch({ initialData })

  const debouncedChangeHandler = useMemo(() => debounce(q => setFilters(filters => ({
    ...(filters ?? {}),
    search: q
  })), 500), [setFilters])

  // Live unseen counts: cheap poll overlay so badges update without refresh.
  // null = not refreshed yet (fall back to Loader counts); after the first
  // successful poll the map is global truth for the viewer (missing = 0).
  const [liveCounts, setLiveCounts] = useState(null)
  useEffect(() => {
    let stopped = false
    const refreshCounts = async () => {
      if (document.hidden) return
      try {
        const counts = await getUnreadClientMessageCounts()
        if (!stopped && counts) setLiveCounts(counts)
      } catch (e) {
        // ignore — next poll / focus retries; existing counts stay visible
      }
    }
    refreshCounts()
    const timer = setInterval(refreshCounts, 30000)
    window.addEventListener('focus', refreshCounts)
    return () => {
      stopped = true
      clearInterval(timer)
      window.removeEventListener('focus', refreshCounts)
      debouncedChangeHandler.cancel() // Cancel pending debounced calls on unmount
    }
  }, [debouncedChangeHandler])

  const loggedInUser = useMemo(() => getLoggedInUserDetails(), [])
  const currentUserId = user?.id || loggedInUser?.userId

  const handleSetFilters = useCallback((filtersOrUpdater) => {
    setFilters((prevFilters) => {
      const current = prevFilters ?? {}
      const next = typeof filtersOrUpdater === 'function' ? filtersOrUpdater(current) : filtersOrUpdater
      const nextClean = { ...(next ?? {}) }
      if (Array.isArray(nextClean.createdAt)) {
        nextClean.createdAt = nextClean.createdAt.filter(Boolean).join(',') || null
      }
      if (!nextClean.account_manager_id) {
        nextClean.show_all = 'true'
      } else {
        delete nextClean.show_all
      }
      savePersistentFilters('project_filters', nextClean)
      return nextClean
    })
  }, [setFilters])

  const listWithLiveCounts = useMemo(() => list.map(item => ({
    ...item,
    unread_client_messages_count: liveCounts
      ? (liveCounts[item.id] || 0)
      : (item.unread_client_messages_count ?? 0),
  })), [list, liveCounts])

  const handleRowClick = useCallback((record) => {
    navigate(generatePath('/projects/:id', {
      id: record.id
    }))
  }, [])

  // Badge click: open the project scrolled straight to Client Communication.
  const handleBadgeClick = useCallback((record) => {
    navigate(`${generatePath('/projects/:id', {
      id: record.id
    })}#client-communication`)
  }, [navigate])

  const viewMode = useMemo(() => searchParams.get('view') ?? getUserSettings('projectListView') ?? VIEW_MODES.KANABAN, [searchParams.get('view')])

  const changeViewMode = (view) => {
    if (viewMode !== view) {
      startTransition(() => {
        setSearchParams(sp => {
          const _sp = new URLSearchParams(sp)
          _sp.set('view', view)
          return _sp
        })
      })
    }
  }

  return (
    <Page className='projects-list' backLink={false} title={props.title} summary={initialData?.meta?.totalCount ? `(${initialData?.meta?.totalCount} Records)` : '(No Records)'} header={
      <>
        <Segmented
          value={viewMode}
          options={[
            {
              value: VIEW_MODES.LIST,
              icon: <BarsOutlined />,
            },
            {
              value: VIEW_MODES.KANABAN,
              icon: <AppstoreOutlined />,
            },
          ]}
          onChange={changeViewMode}
        />
        <Space.Compact style={{ width: '100%', maxWidth: '400px' }}>
          <Input.Search placeholder="Search" onChange={(e) => {
            if (e.target.value === '') {
              debouncedChangeHandler.cancel()
              setSearchParams(sp => {
                const nextParams = new URLSearchParams(sp)
                nextParams.delete('filter[search]')
                nextParams.delete('page')
                nextParams.delete('index')
                return nextParams
              })
              return;
            }
            return debouncedChangeHandler(e.target.value)
          }} defaultValue={searchParamsParsed?.filter?.search ?? ''} allowClear />
          <Button onClick={() => setIsFiltersVisible(v => !v)} icon={<FilterOutlined />}>Filters</Button>
        </Space.Compact>
        <Space align="center">
          <Switch
            checkedChildren="Show Assigned To Me"
            unCheckedChildren="Show All"
            onChange={(checked) => {
              handleSetFilters((prev) => {
                const _filters = { ...prev }
                if (checked) {
                  _filters.account_manager_id = currentUserId
                  delete _filters.show_all
                } else {
                  delete _filters.account_manager_id
                  _filters.show_all = 'true'
                }
                return _filters
              })
            }}
            checked={Boolean(searchParamsParsed?.filter?.account_manager_id)}
          />
          <Button type="primary" onClick={() => { navigate(generateRouteUrl('ProjectCreate')) }} icon={<PlusOutlined />}>Add new Project</Button>
        </Space>
      </>
    } subHeader={initialData.meta?.filters && <SelectedFilters filters={initialData.meta.filters} filterNames={FILTER_NAMES} updateFilters={handleSetFilters} />}>
      {isPending ? <Spin /> : (
        <>
          {viewMode === VIEW_MODES.LIST && <ProjectsListView data={listWithLiveCounts} isLoading={isLoading} hasMore={hasMore} onChangeSort={setSort} onLoadMore={loadMore} onItemClick={handleRowClick} />}
          {viewMode === VIEW_MODES.KANABAN && <ProjectsKanabanView data={listWithLiveCounts} kanabanCount={initialData.kanabanCount} isLoading={isLoading} hasMore={hasMore} onLoadMore={loadMore} onItemClick={handleRowClick} onBadgeClick={handleBadgeClick} />}
        </>
      )}
      <Drawer title="Filters" open={isFiltersVisible} onClose={() => setIsFiltersVisible(false)} >
        <ProjectFilterForm
          key={JSON.stringify(searchParamsParsed.filter)}
          data={searchParamsParsed.filter}
          onFinish={(filters) => {
            handleSetFilters(filters);
            setIsFiltersVisible(false); // Close the Drawer after applying filters
          }}
        />
      </Drawer>
    </Page>
  )
}

Projects.Loader = async ({ request }) => {
  const loggedInUser = getLoggedInUserDetails()
  const fallback = loggedInUser?.userId ? { account_manager_id: loggedInUser.userId } : null
  const savedRedirect = getRedirectWithSavedFilters(request, 'project_filters', fallback)
  if (savedRedirect) return savedRedirect

  const url = new URL(request.url)
  const { filter, page, sort, view = (getUserSettings('projectListView') ?? VIEW_MODES.KANABAN) } = searchParamsToObject(url.searchParams)
  setUserSettings('projectListView', view)
  if (filter?.createdAt && filter.createdAt.split(',')) {
    const [start_date = null, end_date = null] = filter.createdAt.split(',')
    if (start_date) {
      filter.start_date = start_date
      filter.end_date = end_date
      delete filter.createdAt
    }
  }

  const [{ data, meta }, kanabanCount] = await Promise.all([
    getAllProjects({ filter, page, limit: 100, ...sort }),
    view === VIEW_MODES.KANABAN && !page ? getProjectKanabanCount({ filter }) : Promise.resolve(null)
  ])

  return {
    data,
    meta,
    kanabanCount
  }
}

Projects.propTypes = {
  title: PropTypes.string
}

Projects.Skeleton = function Skeleton({ location }) {
  const { view = (getUserSettings('projectListView') ?? VIEW_MODES.KANABAN) } = searchParamsToObject(new URLSearchParams(location?.search))
  if (view === VIEW_MODES.LIST) {
    return (
      <div style={{ width: '100%', padding: '2px 0.5rem 0 0' }}>
        <AntdTable columns={ProjectColumns} className="table--fullwidth" data={[]} loading={true} />
      </div>
    )
  }
  return <KanbanBoardSkeleton columns={4} />
}
Projects.Skeleton.propTypes = {
  location: PropTypes.object
}

export default Projects
